import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
  CellValueChangedEvent,
  ColDef,
  ICellRendererParams,
  IRowNode,
  ValueSetterParams,
} from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import "../../assets/css/AGGrid-custom.css";
import { apiGet, apiPut } from "../../services/genesisApi/httpsMethods";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";
import { useUserInfo } from "../../hooks/useUserInfo";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { ErrorDialog } from "../../components/common/errorDialog";
import {
  DealSidePanel,
  NOTES_PANEL_WIDTH,
} from "../../components/deal/DealSidePanel";
import { dateFormatter, numberFormatter } from "../../utils/numberFormatter";
import {
  createSingleSelectColumn,
  createStringSelectColumn,
} from "../../utils/columnHelpers";
import { areRecordsEqual, getChangedRecords } from "../../utils/compareData";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { CustomButton } from "../../components/common/customButton";
import { CustomIconButton } from "../../components/common/customIconButton";
import { CustomMultiSelect } from "../../components/common/inputFields/customMultiSelect";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import type { Deal, KillReason, Status, User } from "../../types/deal";
import { KillReasonDialog } from "@/components/deal/KillReasonDialog";
import { hasKillReason, isKilledStatus } from "@/utils/dealStatusHelpers";
import { getStatusName } from "@/utils/dealValidation";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { useCurrentUser } from "@/context/CurrentUserContext";
import pencilIcon from "@/assets/svg/edit.svg";
import { NotificationBellButton } from "@/components/notifications/NotificationBell";
import { DataCompletenessButton } from "@/components/dataCompleteness/DataCompletenessButton";
import {
  dealPriorityComparator,
  PRIORITY_OPTIONS,
  PriorityRenderer,
  PROBABILITY_OPTIONS,
  ProbabilityBarRenderer,
} from "../pipeline/pipelineColumnDefs";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

const STATUS_FILTER_DEFAULT_DESELECTED = [
  "Closed",
  "Killed",
  "Lost to other lender",
  "Referred to Apollo",
];

ModuleRegistry.registerModules([AllCommunityModule]);

const isMsalEnabled = import.meta.env.VITE_MSAL_ENABLED === "true";

function DealLinkRenderer(params: ICellRendererParams<Deal>) {
  const data = params.data;
  const name = params.value;
  const useId =
    data?.id != null && typeof data.id === "number" && data.id >= 0
      ? data.id
      : null;
  const useParentId =
    useId == null &&
    data?.parent_deal_id != null &&
    typeof data.parent_deal_id === "number"
      ? data.parent_deal_id
      : null;
  const linkId = useId ?? useParentId;
  if (linkId == null) return name || "-";
  return React.createElement(
    "a",
    {
      href: `/deals/${linkId}`,
      style: { color: "#1B7A63", textDecoration: "underline" },
    },
    name || `ID ${data?.id ?? linkId}`,
  );
}

function buildMyDashboardColumnDefs(args: {
  editMode: boolean;
  status: { id: number; status: string }[];
  users: { id: number; name: string }[];
  /** Matches `useReferenceLists().currencies` (ISO codes). */
  currencies: string[];
}): ColDef<Deal>[] {
  const { editMode, status, users, currencies } = args;
  const currencyStrings = currencies ?? [];

  const getEditable = (editable: boolean) => (editMode ? editable : false);

  return [
    {
      ...createSingleSelectColumn(
        "status",
        "Status",
        status,
        "status",
        160,
        editMode,
      ),
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
    {
      field: "deal_name",
      headerName: "Deal Name",
      width: 260,
      cellRenderer: DealLinkRenderer,
      flex: 1,
      minWidth: 200,
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
    {
      ...createSingleSelectColumn(
        "deal_lead",
        "Deal Lead",
        users,
        "name",
        110,
        editMode,
      ),
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
    {
      ...createSingleSelectColumn(
        "deal_support",
        "Deal Support",
        users,
        "name",
        110,
        editMode,
      ),
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
    {
      field: "loan_amount_fully_funded",
      headerName: "Total Loan Amount",
      width: 120,
      valueFormatter: numberFormatter,
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
    {
      ...createStringSelectColumn(
        "loan_amount_currency",
        "Loan Currency",
        currencyStrings,
        95,
        editMode,
      ),
      editable: getEditable(true),
      sortable: true,
      filter: false,
      valueSetter: (params: ValueSetterParams<Deal, string | null>) => {
        if (params.data) {
          const v = params.newValue;
          params.data.loan_amount_currency =
            v == null
              ? null
              : typeof v === "string"
                ? v
                : (v as { id?: string; label?: string })?.id ??
                  (v as { id?: string; label?: string })?.label ??
                  null;
        }
        return true;
      },
    },
    {
      field: "target_closing",
      headerName: "Expected Closing Date",
      width: 130,
      valueFormatter: dateFormatter,
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
    {
      field: "proposed_ic_date",
      headerName: "IC Date",
      width: 100,
      valueFormatter: dateFormatter,
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
    {
      ...createSingleSelectColumn(
        "probability",
        "Probability (%)",
        PROBABILITY_OPTIONS,
        "name",
        140,
        editMode,
      ),
      editable: getEditable(true),
      cellRenderer: ProbabilityBarRenderer,
      // Deal.probability is number | null — store scalar for API/save, not { id, label }
      valueSetter: (params: ValueSetterParams<Deal, unknown>) => {
        const nv = params.newValue;
        const asSelect = nv as { id?: number | null } | null | undefined;
        if (
          nv === null ||
          nv === undefined ||
          (typeof nv === "object" && nv !== null && asSelect?.id === null)
        ) {
          if (params.data) params.data.probability = null;
          return true;
        }
        if (
          typeof nv === "object" &&
          nv !== null &&
          asSelect?.id !== undefined
        ) {
          if (params.data) params.data.probability = asSelect.id ?? null;
          return true;
        }
        return false;
      },
      // Numeric sort on Deal.probability (25/50/75/100); nulls last. Grid negates
      // this for desc — use isDescending so empty cells stay last in both directions.
      comparator: (
        _valueA: unknown,
        _valueB: unknown,
        nodeA: IRowNode<Deal>,
        nodeB: IRowNode<Deal>,
        isDescending: boolean,
      ) => {
        const pa = nodeA?.data?.probability;
        const pb = nodeB?.data?.probability;
        const na =
          typeof pa === "number" && !Number.isNaN(pa) ? pa : null;
        const nb =
          typeof pb === "number" && !Number.isNaN(pb) ? pb : null;
        if (na === null && nb === null) return 0;
        if (na === null) return isDescending ? -1 : 1;
        if (nb === null) return isDescending ? 1 : -1;
        return na - nb;
      },
      sortable: true,
      filter: false,
    },
    {
      ...createSingleSelectColumn(
        "priority",
        "Priority",
        PRIORITY_OPTIONS,
        "name",
        120,
        editMode,
      ),
      editable: getEditable(true),
      cellRenderer: PriorityRenderer,
      // Deal.priority is string | null — store scalar for API/save, not { id, label }
      valueSetter: (params: ValueSetterParams<Deal, unknown>) => {
        const nv = params.newValue;
        if (
          nv === null ||
          nv === undefined ||
          (typeof nv === "object" &&
            nv !== null &&
            (nv as { id?: string | null }).id === null)
        ) {
          if (params.data) params.data.priority = null;
          return true;
        }
        if (
          typeof nv === "object" &&
          nv !== null &&
          (nv as { id?: string }).id !== undefined
        ) {
          if (params.data)
            params.data.priority = (nv as { id: string }).id ?? null;
          return true;
        }
        return false;
      },
      // Ordinal sort: asc High → Medium → Low; desc Low → Medium → High; nulls last
      comparator: dealPriorityComparator,
      sortable: true,
      filter: false,
    },
    {
      field: "has_complete_ten_pager",
      headerName: "Ten Pager",
      width: 85,
      valueFormatter: (params) =>
        params.value == null ? "-" : params.value ? "Yes" : "No",
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
    {
      field: "has_complete_term_sheet",
      headerName: "Term Sheet",
      width: 85,
      valueFormatter: (params) =>
        params.value == null ? "-" : params.value ? "Yes" : "No",
      editable: getEditable(true),
      sortable: true,
      filter: false,
    },
  ] as ColDef<Deal>[];
}

const MyDashboard = () => {
  const { account } = useUserInfo();
  const { canEdit } = useCurrentUser();
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const gridRef = useRef<AgGridReact>(null);

  const [deals, setDeals] = useState<Deal[]>([]);
  const [initialDeals, setInitialDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDealId, setSelectedDealId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // A move to Killed waiting for a kill reason; the cell shows the old status until then.
  const [pendingKill, setPendingKill] = useState<{
    id: number;
    dealName: string;
    status: Deal["status"];
  } | null>(null);
  const [dealNameFilter, setDealNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const statusFilterInitialized = useRef(false);

  const { status, users, currencies } = useReferenceLists() as {
    status: Status[];
    users: User[];
    currencies: string[];
  };

  useEffect(() => {
    if (status?.length && !statusFilterInitialized.current) {
      const defaultStatuses = status.filter(
        (s) => !STATUS_FILTER_DEFAULT_DESELECTED.includes(s.status),
      );
      setStatusFilter(defaultStatuses);
      statusFilterInitialized.current = true;
    }
  }, [status]);

  const oid = useMemo(() => {
    if (!account) return null;
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    return (claims?.oid as string) ?? account.localAccountId ?? null;
  }, [account]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = isMsalEnabled && oid ? { msal_oid: oid } : undefined;
    apiGet<Deal[]>("deals", params)
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          const list = Array.isArray(response.data) ? response.data : [];
          setDeals(list);
          setInitialDeals(structuredClone(list));
        } else {
          showErrorFromResponse(response);
          setDeals([]);
          setInitialDeals([]);
        }
      })
      .catch(() => {
        if (cancelled) return;
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch deals. Please try again.",
        });
        setDeals([]);
        setInitialDeals([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [oid]);

  const dirtyGrid = !areRecordsEqual(deals, initialDeals);

  const handleCancel = useCallback(() => {
    setDeals(structuredClone(initialDeals));
    setEditMode(false);
  }, [initialDeals]);

  const handleSave = useCallback(async () => {
    if (isSaving) return;
    const changed = getChangedRecords(deals, initialDeals);
    setIsSaving(true);
    let failedResponse: ApiResponse | undefined;
    try {
      for (const item of changed) {
        const id = item.id;
        if (id == null || typeof id !== "number") continue;
        const { id: _id, ...payload } = item;
        const sourceEntity = (payload as any).source_entity;
        if (
          sourceEntity &&
          typeof sourceEntity === "object" &&
          typeof sourceEntity.id === "number"
        ) {
          (payload as any).source_id = sourceEntity.id;
          delete (payload as any).source_entity;
          delete (payload as any).source;
        }
        const response = await apiPut("deal", id, payload);
        if (!response?.success && !failedResponse) failedResponse = response;
      }
    } catch {
      failedResponse = {
        success: false,
        status: 500,
        message: "Failed to save deal changes. Please try again.",
      };
    }
    if (failedResponse) {
      // Stay in edit mode and keep the user's edits so they can retry.
      setIsSaving(false);
      showErrorFromResponse(failedResponse);
      return;
    }
    setInitialDeals(structuredClone(deals));
    setEditMode(false);
    window.location.reload();
  }, [deals, initialDeals, isSaving, showErrorFromResponse]);

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSave);

  const onCellValueChanged = useCallback((event: CellValueChangedEvent<Deal>) => {
    const { colDef, newValue, oldValue, data } = event;
    const field = colDef.field;
    if (!data?.id || !field) return;
    const isValueEqual = (a: any, b: any): boolean => {
      if (a === b) return true;
      if (a?.id && b?.id) return a.id === b.id;
      if (Array.isArray(a) && Array.isArray(b)) {
        return (
          a.length === b.length &&
          a.every((item, index) => isValueEqual(item, b[index]))
        );
      }
      return false;
    };
    if (isValueEqual(newValue, oldValue)) return;
    // Prefer row data after valueSetter (scalars for probability, objects for relations)
    const key = field as keyof Deal;
    const committed = data[key];
    if (
      key === "status" &&
      isKilledStatus(getStatusName(committed)) &&
      !hasKillReason(data)
    ) {
      (data as unknown as Record<string, unknown>).status = oldValue;
      event.api.refreshCells({
        rowNodes: [event.node],
        columns: ["status"],
        force: true,
      });
      setPendingKill({
        id: data.id,
        dealName: data.deal_name || "Unnamed deal",
        status: committed as Deal["status"],
      });
      return;
    }
    setDeals((prev) =>
      prev.map((d) => (d.id === data.id ? { ...d, [key]: committed } : d)),
    );
  }, []);

  const confirmKill = useCallback(
    (reason: KillReason | null) => {
      if (!pendingKill) return;
      const { id, status: killedStatus } = pendingKill;
      setPendingKill(null);
      setDeals((prev) =>
        prev.map((d) =>
          d.id === id
            ? {
                ...d,
                status: killedStatus,
                ...(reason ? { kill_reason: reason } : {}),
              }
            : d,
        ),
      );
    },
    [pendingKill],
  );

  const onRowClicked = useCallback((event: { data?: Deal }) => {
    const id = event.data?.id;
    if (typeof id === "number" && id > 0) setSelectedDealId(id);
  }, []);

  const columnDefs = useMemo(
    () =>
      buildMyDashboardColumnDefs({
        editMode,
        status: status ?? [],
        users: users ?? [],
        currencies: currencies ?? [],
      }),
    [editMode, status, users, currencies],
  );

  const filteredDeals = useMemo(() => {
    let result = deals;
    if (statusFilter.length) {
      const ids = new Set(statusFilter.map((s) => s.id));
      result = result.filter((d) => d.status && ids.has(d.status.id));
    }
    if (dealNameFilter.trim()) {
      const q = dealNameFilter.toLowerCase().trim();
      result = result.filter((d) => d.deal_name?.toLowerCase().includes(q));
    }
    return result;
  }, [deals, statusFilter, dealNameFilter]);

  useEffect(() => {
    if (filteredDeals.length === 0) return;
    const selectedInList =
      selectedDealId != null &&
      selectedDealId > 0 &&
      filteredDeals.some((d) => d.id === selectedDealId);
    if (!selectedInList) {
      const first = filteredDeals[0];
      const firstId = first?.id;
      if (typeof firstId === "number" && firstId > 0)
        setSelectedDealId(firstId);
    }
  }, [filteredDeals, selectedDealId]);

  const selectedDeal = useMemo(
    () =>
      selectedDealId != null
        ? deals.find((d) => d.id === selectedDealId)
        : null,
    [deals, selectedDealId],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        padding: "0px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <SimpleToolbar>
          <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <CustomTextInput
              label="Deal name"
              value={dealNameFilter}
              fullWidth={false}
              onChange={(val) => {
                if (typeof val === "string") setDealNameFilter(val);
              }}
            />
            <CustomMultiSelect
              value={statusFilter.map((s) => s.id)}
              onChange={(e) => {
                const selectedIds =
                  (e.target.value as (string | number)[]) ?? [];
                const selected = (status ?? []).filter((s) =>
                  selectedIds.includes(s.id),
                );
                setStatusFilter(selected);
              }}
              options={status ?? []}
              placeholder="Select Status"
              labelKey="status"
              minWidth="200px"
            />
          </Box>
          {editMode ? (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: "15px" }}
              data-position="right"
            >
              <CustomButton
                handleButtonPress={handleCancel}
                isButtonDisabled={isSaving}
                text="Cancel"
                isLoading={false}
                isLoadingText="something"
                solid={false}
                widthOverride="100px"
              />
              <CustomButton
                handleButtonPress={requestSave}
                isButtonDisabled={(!dirtyGrid && !isCellEditing) || isSaving}
                text="Save"
                isLoading={isSaving}
                isLoadingText="Saving"
                solid
                widthOverride="100px"
              />
            </Box>
          ) : null}
          {!editMode && (
            <Box
              data-position="right"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                ml: -0.5,
                "& .MuiIconButton-root": {
                  p: 0.6,
                },
                "& .MuiIconButton-root .MuiSvgIcon-root": {
                  fontSize: 22,
                },
              }}
            >
              <DataCompletenessButton />
              <NotificationBellButton />
              <CustomIconButton
                tooltip="Edit"
                icon={
                  <Box
                    component="img"
                    src={pencilIcon}
                    alt="Edit dashboard"
                    sx={{ width: 20, height: 20, display: "block" }}
                  />
                }
                disabled={!canEdit}
                onClick={() => {
                  if (!canEdit) return;
                  setEditMode(true);
                }}
              />
            </Box>
          )}
        </SimpleToolbar>
      </Box>

      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            padding: "0px 20px 8px 20px",
            minWidth: 0,
          }}
        >
          {deals.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: 200,
              }}
            >
              <Typography color="text.secondary">
                {isMsalEnabled && !oid
                  ? "Sign in to see your deals."
                  : "You have no deals assigned."}
              </Typography>
            </Box>
          ) : filteredDeals.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: 200,
              }}
            >
              <Typography color="text.secondary">
                No deals match the selected status filter.
              </Typography>
            </Box>
          ) : (
            <div
              className="ag-theme-alpine"
              style={{ width: "100%", height: "100%", marginTop: "8px" }}
            >
              <AgGridReact<Deal>
                {...gridEditProps}
                ref={gridRef}
                rowData={filteredDeals}
                columnDefs={columnDefs}
                onRowClicked={onRowClicked}
                onCellValueChanged={onCellValueChanged}
                singleClickEdit
                defaultColDef={{
                  resizable: true,
                  filter: false,
                  sortable: false,
                }}
                rowHeight={27}
                getRowId={({ data }) => data?.id?.toString() ?? ""}
                getRowStyle={(params) =>
                  params.data?.id === selectedDealId
                    ? { backgroundColor: "rgba(36, 94, 160, 0.08)" }
                    : undefined
                }
              />
            </div>
          )}
        </Box>

        <Box
          sx={{
            width: NOTES_PANEL_WIDTH,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            borderLeft: "2px solid",
            borderColor: "divider",
            boxShadow: "-2px 0 6px rgba(0, 0, 0, 0.06)",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <DealSidePanel
            dealId={selectedDealId}
            dealName={selectedDeal?.deal_name}
          />
        </Box>
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
      <KillReasonDialog
        open={pendingKill !== null}
        dealName={pendingKill?.dealName ?? ""}
        onCancel={() => setPendingKill(null)}
        onConfirm={confirmKill}
      />
    </Box>
  );
};

export default MyDashboard;
